import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiUpload, FiLayers, FiDollarSign, FiTruck, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabase/supabase';
import { useNavigate } from 'react-router-dom';

// Helper to reliably get local YYYY-MM-DD for grouping
const getLocalYYYYMMDD = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// --- ADMIN DASHBOARD COMPONENT ---
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, active: 0, exceptions: 0, payout: 0 });
  const [chartData, setChartData] = useState({ categories: [], seriesData: [] });
  const [recentClaims, setRecentClaims] = useState([]);
  const [recentExceptions, setRecentExceptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminDashboardData();
  }, []);

  const fetchAdminDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch core claims data
      const { data, error } = await supabase
        .from('claims_1783447171616')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      let t = 0, a = 0, e = 0, p = 0;
      const issues = [];
      
      // Initialize map for the last 7 days (for the chart)
      const last7DaysMap = {};
      const categories = [];

      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dateKey = getLocalYYYYMMDD(d);
        const label = d.toLocaleDateString('en-US', { weekday: 'short' });
        last7DaysMap[dateKey] = 0;
        categories.push(label);
      }

      data.forEach(c => {
        t++; // Total Claims
        if (c.status === 'Assigned' || c.status === 'Accepted') a++; // Active Recoveries
        if (c.status === 'Issue') {
          e++; // Exceptions
          issues.push(c);
        }
        if (c.payment_status === 'Paid') {
          p += parseFloat(c.offer_price || c.estimated_value || 0);
        }

        // Map claim to chart if created within the last 7 days
        if (c.created_at) {
          const claimDateKey = getLocalYYYYMMDD(c.created_at);
          if (last7DaysMap[claimDateKey] !== undefined) {
            last7DaysMap[claimDateKey]++;
          }
        }
      });

      const seriesData = Object.values(last7DaysMap);

      setStats({ total: t, active: a, exceptions: e, payout: p });
      setRecentClaims(data.slice(0, 5)); // top 5 newest
      setRecentExceptions(issues.slice(0, 5)); // top 5 issues
      setChartData({ categories, seriesData });

    } catch (err) {
      console.error("Error fetching admin stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const barChartOptions = {
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: chartData.categories.length ? chartData.categories : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#9ca3af' }
    },
    yAxis: { type: 'value', show: false },
    series: [
      {
        data: chartData.seriesData.length ? chartData.seriesData : [0, 0, 0, 0, 0, 0, 0],
        type: 'bar',
        itemStyle: { color: '#e6f3ec', borderRadius: [4, 4, 0, 0] },
        emphasis: { itemStyle: { color: '#1a5d3a' } }
      }
    ]
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Admin Dashboard</h1>
          <p className="text-gray-500">Monitor overall salvage operations and exceptions.</p>
        </div>
        <div className="flex gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-sm transition-colors">
            <SafeIcon icon={FiUpload} /> Import Excel
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand-light font-medium text-sm transition-colors shadow-sm">
            <SafeIcon icon={FiPlus} /> Manual Claim
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card dark className="relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start mb-4 relative z-10">
            <h3 className="text-white/80 font-medium">Total Claims</h3>
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <SafeIcon icon={FiLayers} className="text-white" />
            </div>
          </div>
          <div className="relative z-10">
            <h2 className="text-4xl font-bold mb-2">{stats.total}</h2>
            <p className="text-sm text-brand-accent flex items-center gap-1">
              All claims in system
            </p>
          </div>
          <div className="absolute -right-4 -top-4 w-32 h-32 bg-white opacity-5 rounded-full blur-2xl"></div>
        </Card>

        <Card className="flex flex-col justify-between">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-gray-500 font-medium">Active Recoveries</h3>
            <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center">
              <SafeIcon icon={FiTruck} className="text-gray-400" />
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-bold text-gray-800 mb-2">{stats.active}</h2>
            <p className="text-sm text-gray-500">Assigned & Accepted</p>
          </div>
        </Card>

        <Card className="flex flex-col justify-between">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-gray-500 font-medium">Exceptions</h3>
            <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center">
              <SafeIcon icon={FiAlertCircle} className="text-gray-400" />
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-bold text-gray-800 mb-2">{stats.exceptions}</h2>
            <p className="text-sm text-gray-500">Requires manual review</p>
          </div>
        </Card>

        <Card className="flex flex-col justify-between border-l-4 border-green-500">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-gray-500 font-medium">Total Payout</h3>
            <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="text-green-600" />
            </div>
          </div>
          <div>
            <h2 className="text-2xl xl:text-3xl font-bold text-gray-800 mb-2">
              R {stats.payout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <p className="text-sm text-gray-500">Paid across all salvagers</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-semibold text-gray-800">Claims Received (7 Days)</h3>
          </div>
          <div className="h-48">
            <ReactECharts option={barChartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </Card>

        <Card className="lg:col-span-1 flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="font-semibold text-gray-800">Exceptions Queue</h3>
              <p className="text-xs text-gray-500">Requires manual review</p>
            </div>
            <button onClick={() => navigate('/exceptions')} className="text-brand text-sm font-medium hover:underline">View All</button>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto max-h-[300px] pr-2">
            {!loading && recentExceptions.length === 0 && (
              <div className="text-center text-gray-400 py-8 italic text-sm">
                No active exceptions.
              </div>
            )}
            {recentExceptions.map(ex => (
              <div key={ex.id} className="flex items-center justify-between p-3 rounded-xl bg-orange-50/50 hover:bg-orange-50 transition-colors border border-orange-100/50">
                <div>
                  <p className="text-sm font-bold text-gray-800">{ex.claim_number}</p>
                  <p className="text-xs text-red-500 font-medium line-clamp-1">{ex.ber_reason}</p>
                </div>
                <button onClick={() => navigate('/exceptions')} className="px-3 py-1.5 bg-orange-500 text-white text-xs rounded-lg font-bold shadow-sm hover:bg-orange-600 transition-colors">
                  Resolve
                </button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-1 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-semibold text-gray-800">Recent Claims</h3>
            <button onClick={() => navigate('/claims')} className="text-brand text-sm font-medium hover:underline">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {!loading && recentClaims.length === 0 && (
              <div className="text-center text-gray-400 py-8 italic text-sm">
                No claims yet.
              </div>
            )}
            {recentClaims.map(claim => (
              <div key={claim.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    claim.status === 'New' ? 'bg-blue-100 text-blue-600' :
                    claim.status === 'Assigned' ? 'bg-yellow-100 text-yellow-600' :
                    claim.status === 'Issue' ? 'bg-orange-100 text-orange-600' :
                    'bg-green-100 text-green-600'
                  }`}>
                    <SafeIcon icon={FiLayers} size={14} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">{claim.claim_number}</p>
                    <p className="text-xs text-gray-500">{claim.salvage_item || claim.make || 'Unknown Item'}</p>
                  </div>
                </div>
                <Badge status={claim.status}>{claim.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

// --- SALVAGER DASHBOARD COMPONENT ---
const SalvagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ new: 0, accepted: 0, paid: 0, payout: 0 });
  const [recentClaims, setRecentClaims] = useState([]);

  useEffect(() => {
    if (user?.id) fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from('claims_1783447171616')
        .select('*')
        .eq('assigned_salvager_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      let newC = 0, acceptedC = 0, paidC = 0, payoutTotal = 0;
      
      data.forEach(claim => {
        if (claim.payment_status === 'Paid') {
          paidC++;
          payoutTotal += parseFloat(claim.offer_price || claim.estimated_value || 0);
        } else if (claim.status === 'Assigned') {
          newC++;
        } else if (claim.status === 'Accepted') {
          acceptedC++;
        }
      });

      setStats({ new: newC, accepted: acceptedC, paid: paidC, payout: payoutTotal });
      setRecentClaims(data.slice(0, 5));
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">My Dashboard</h1>
          <p className="text-gray-500">Overview of your assigned and processed salvage claims.</p>
        </div>
      </div>

      <AnimatePresence>
        {stats.new > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 relative overflow-hidden"
          >
            <motion.div
              animate={{
                boxShadow: ["0px 0px 0px rgba(59, 130, 246, 0)", "0px 0px 20px rgba(59, 130, 246, 0.5)", "0px 0px 0px rgba(59, 130, 246, 0)"]
              }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-lg border border-blue-400/50 flex flex-col md:flex-row items-center justify-between gap-4 relative z-10"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
                  <SafeIcon icon={FiAlertCircle} size={28} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">New Claims Alert!</h2>
                  <p className="text-blue-100 text-sm mt-1">
                    You have <span className="font-bold text-white text-base px-1">{stats.new}</span> new claim(s) waiting. Please accept them before the 24-hour timer expires.
                  </p>
                </div>
              </div>
              <button 
                onClick={() => navigate('/my-claims')} 
                className="px-6 py-3 bg-white text-blue-600 font-bold rounded-xl shadow-md hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                View New Claims
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="flex flex-col justify-between border-l-4 border-blue-500">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-gray-500 font-medium tracking-wide text-sm uppercase">New Claims</h3>
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <SafeIcon icon={FiLayers} className="text-blue-500 text-xl" />
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-bold text-gray-800">{stats.new}</h2>
            <p className="text-sm text-gray-500 mt-1">Awaiting your acceptance</p>
          </div>
        </Card>

        <Card className="flex flex-col justify-between border-l-4 border-yellow-500">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-gray-500 font-medium tracking-wide text-sm uppercase">Accepted & Pending</h3>
            <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center">
              <SafeIcon icon={FiTruck} className="text-yellow-600 text-xl" />
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-bold text-gray-800">{stats.accepted}</h2>
            <p className="text-sm text-gray-500 mt-1">Currently in progress</p>
          </div>
        </Card>

        <Card className="flex flex-col justify-between border-l-4 border-green-500">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-gray-500 font-medium tracking-wide text-sm uppercase">Paid & Completed</h3>
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <SafeIcon icon={FiCheckCircle} className="text-green-600 text-xl" />
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-bold text-gray-800">{stats.paid}</h2>
            <p className="text-sm text-gray-500 mt-1">Successfully finalized</p>
          </div>
        </Card>

        <Card className="flex flex-col justify-between border-l-4 border-brand">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-gray-500 font-medium tracking-wide text-sm uppercase">Total Payout</h3>
            <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="text-brand text-xl" />
            </div>
          </div>
          <div>
            <h2 className="text-2xl xl:text-3xl font-bold text-gray-800 pt-1">
              R {stats.payout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <p className="text-sm text-gray-500 mt-1">Total earnings</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <Card className="lg:col-span-2 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-semibold text-gray-800">Recent Assignments</h3>
            <button onClick={() => navigate('/my-claims')} className="text-brand text-sm font-medium hover:underline">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentClaims.length === 0 ? (
              <div className="text-center text-gray-400 py-10 italic text-sm">
                No recent assignments found.
              </div>
            ) : (
              recentClaims.map(claim => (
                <div key={claim.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      claim.status === 'New' ? 'bg-blue-100 text-blue-600' :
                      claim.status === 'Assigned' ? 'bg-yellow-100 text-yellow-600' :
                      claim.status === 'Issue' ? 'bg-orange-100 text-orange-600' :
                      'bg-green-100 text-green-600'
                    }`}>
                      <SafeIcon icon={FiLayers} size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">{claim.claim_number}</p>
                      <p className="text-xs text-gray-500">{claim.salvage_item || claim.make || 'Unknown Item'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {claim.offer_price > 0 && (
                      <span className="font-bold text-gray-900 bg-white px-2 py-1 rounded-md border border-gray-100 text-xs shadow-sm">
                        R {parseFloat(claim.offer_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                    <Badge status={claim.status}>{claim.status}</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="lg:col-span-1 bg-brand-dark text-white relative overflow-hidden flex flex-col justify-center items-center text-center p-8">
          <div className="relative z-10">
            <div className="w-16 h-16 rounded-3xl bg-white/10 flex items-center justify-center mb-6 mx-auto">
              <SafeIcon icon={FiTruck} size={32} />
            </div>
            <h3 className="text-xl font-bold mb-2">Ready to work?</h3>
            <p className="text-brand-accent/80 text-sm mb-8">
              Check your "My Claims" tab to accept and process new salvage requests sent by the insurer.
            </p>
            <button onClick={() => navigate('/my-claims')} className="w-full py-3 bg-white text-brand-dark rounded-xl font-bold hover:bg-gray-100 transition-colors shadow-lg">
              Go to My Claims
            </button>
          </div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-brand rounded-full opacity-50 blur-3xl"></div>
        </Card>
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const { profile } = useAuth();
  
  if (profile?.role === 'SUPER_ADMIN') {
    return <AdminDashboard />;
  }

  return <SalvagerDashboard />;
};